import React from "react";
import { useQuery } from "@apollo/client";
import { withRouter, Router, Match } from "found";

// web-settings
import { webSettingsQuery } from "./WebSettings.graphql";
import { WebSettingsDrawer } from "./web-settings-drawer";
import {
  WebSettingsContext,
  WebSettingsSections,
  AccountSections,
  TeamsSections,
  OrganizationSections,
  InternalSections,
  getSettingsViewIdForRoute,
  createSettingsViewId,
  getExpandableTreeItemFromViewId,
} from "./web-settings";

import { WebSettingsEmailsAndNotifications } from "./web-settings-emails-and-notifications";
import { WebSettingsEventColorCodingRelay } from "./web-settings-event-color-coding";
import { WebSettingsExperiments } from "./web-settings-experiments";
import { WebSettingsHoursAndPreferences } from "./web-settings-hours-and-preferences";
import { WebSettingsIntegrations } from "./web-settings-integrations";
import { WebSettingsOrganizationAnalytics } from "./web-settings-organization-analytics";
import { WebSettingsOrganizationMembers } from "./web-settings-organization-members";
import { WebSettingsOrganizationPlansAndBilling } from "./web-settings-organization-plans-and-billing";
import { WebSettingsOrganizationSettings } from "./web-settings-organization-settings";
import { WebSettingsSmartHoldsRelay } from "./web-settings-smart-holds";

// other components
import { NoRouteView } from "@components/no-route-view";
import { LoaderClock } from "@clockwise/web-commons/src/components/loader-clock";

// constants
import { paths } from "@clockwise/client-commons/src/constants/site";

// util
import { getBulkExperiments } from "@src/util/experiment.util";
import { getCurrentOrg } from "@src/util/org.util";
import { PageEvents, page } from "@clockwise/web-commons/src/util/analytics.util";
import { logger } from "@src/util/logger.util";

type Props = {
  router: Router;
  match: Match;
};

function WebSettingsCmpt(props: Props) {
  const { data } = useQuery<any>(webSettingsQuery);
  const org = getCurrentOrg(data?.viewer || null);

  const [settingsViewId, setSettingsViewId] = React.useState(
    getSettingsViewIdForRoute(window.location) ||
      createSettingsViewId(WebSettingsSections.Account, AccountSections.HoursAndPreferences),
  );
  window.history.pushState("", "", `${paths.webApp}${paths.webSettings}`);

  React.useEffect(() => {
    page(PageEvents.WEB_SETTINGS);
  }, []);

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  console.log(settingsViewId);
  if (!org) {
    return <LoaderClock />;
  } else if (!getBulkExperiments(org).WebSettings) {
    return <NoRouteView />;
  }

  const renderCurrentView = () => {
    const parentTreeItem = getExpandableTreeItemFromViewId(settingsViewId);
    switch (parentTreeItem) {
      case createSettingsViewId(WebSettingsSections.Account):
      case createSettingsViewId(WebSettingsSections.Account, AccountSections.HoursAndPreferences):
        return <WebSettingsHoursAndPreferences />;
      case createSettingsViewId(WebSettingsSections.Account, AccountSections.SmartHolds):
        return <WebSettingsSmartHoldsRelay />;
      case createSettingsViewId(WebSettingsSections.Account, AccountSections.Integrations):
        return <WebSettingsIntegrations />;
      case createSettingsViewId(WebSettingsSections.Account, AccountSections.EventColorCoding):
        return <WebSettingsEventColorCodingRelay />;
      case createSettingsViewId(
        WebSettingsSections.Account,
        AccountSections.TeamAvailabilityCalendar,
      ):
        return <div>TeamAvailabilityCalendar</div>;
      case createSettingsViewId(WebSettingsSections.Account, AccountSections.TeamNoMeetingDay):
        return <div>TeamNoMeetingDay</div>;
      case createSettingsViewId(
        WebSettingsSections.Account,
        AccountSections.EmailsAndNotifications,
      ):
        return <WebSettingsEmailsAndNotifications onChangeSettingsView={setSettingsViewId} />;
      case createSettingsViewId(WebSettingsSections.Teams):
      case createSettingsViewId(WebSettingsSections.Teams, TeamsSections.CreateOrJoinTeam):
        return <div>Teams</div>;
      case createSettingsViewId(WebSettingsSections.Organization):
      case createSettingsViewId(WebSettingsSections.Organization, OrganizationSections.Analytics):
        return <WebSettingsOrganizationAnalytics />;
      case createSettingsViewId(WebSettingsSections.Organization, OrganizationSections.Members):
        return <WebSettingsOrganizationMembers match={props.match} />;
      case createSettingsViewId(WebSettingsSections.Organization, OrganizationSections.Teams):
        return <div>Teams</div>;
      case createSettingsViewId(
        WebSettingsSections.Organization,
        OrganizationSections.PlansAndBilling,
      ):
        return <WebSettingsOrganizationPlansAndBilling />;
      case createSettingsViewId(
        WebSettingsSections.Organization,
        OrganizationSections.OrganizationSettings,
      ):
        return <WebSettingsOrganizationSettings />;
      case createSettingsViewId(WebSettingsSections.Internal):
      case createSettingsViewId(WebSettingsSections.Internal, InternalSections.Experiments):
        return <WebSettingsExperiments />;
      default:
        logger.error("failed to renderCurrentView() in web settings");
        return "No view available";
    }
  };

  return (
    <WebSettingsContext.Provider value={{ settingsViewId }}>
      <div className="cw-w-full cw-h-full cw-flex cw-flex-row">
        <WebSettingsDrawer settingsViewId={settingsViewId} onSelect={setSettingsViewId} />
        {renderCurrentView()}
      </div>
    </WebSettingsContext.Provider>
  );
}

export const WebSettings = withRouter(WebSettingsCmpt) as React.ComponentClass<{}>;
